import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageEntity } from '@interpals/api-web';

import { languagesWithLevelsOptions } from 'utils/options';
import { USER_LANGUAGES_MAX_LEN } from 'constants/limits';
import { createCustomToast } from 'services/toasts';

type Values = {
    know: string[];
    learn: string[];
};

export const langEntityToString = (lang: LanguageEntity) => lang.lang + lang.level;

// This hook contains common logic for updating a user's account languages and is
// shared between onboarding and edit profile's BasicInformation components
const useLanguageUpdater = <T extends Values>(values: T, setValues: (values: T) => void) => {
    const { t } = useTranslation();

    const addUniqueLangToLangLevels = (langsWithLevels: string[], newLangWithLevel: string) => {
        const langNoLevel = newLangWithLevel.substring(0, 2);

        return [...langsWithLevels.filter((langLvl) => langLvl.substring(0, 2) !== langNoLevel), newLangWithLevel];
    };

    const addLanguage = (langWithLevel: string, fieldName: 'know' | 'learn'): void => {
        const langs = addUniqueLangToLangLevels(values[fieldName], langWithLevel);

        if (langs.length > USER_LANGUAGES_MAX_LEN) {
            createCustomToast('error', `${t('Validation.MaxSelected')}`);

            return;
        }

        setValues({
            ...values,
            [fieldName]: langs,
        });
    };

    const langOptions = useMemo(() => languagesWithLevelsOptions(), []);

    const knowOptions = useMemo(() => {
        const knownLangsSet = new Set(values.know);

        return langOptions.filter((item) => knownLangsSet.has(item.value)) || [];
    }, [langOptions, values.know]);

    const learnOptions = useMemo(() => {
        const learnLangsSet = new Set(values.learn);

        return langOptions.filter((item) => learnLangsSet.has(item.value)) || [];
    }, [langOptions, values.learn]);

    return { addLanguage, knowOptions, learnOptions };
};

export default useLanguageUpdater;
